import useSummary from "../../../useSummary";
import React, { useEffect, useState } from "react";
import useTrend from "../../Trends/useTrend";
import { timeScales } from "../../../DashboardUtils";
import "./KPICard.scss";
import { capitalize, snakeCase } from "lodash-es";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  Loader,
  SkeletonLoader,
  normalizeNumbers,
  formatNumber,
} from "@SeemplicityDev/shared";
import WidgetError from "../../WidgetError/WidgetError";
import useSeverityAsPriority, {
  SeverityFieldsEnum,
} from "../../../../General/useSeverityAsPriority";
import useWidgetFilterNavigator, {
  type FilterCondition,
} from "../../../useWidgetFilterNavigator";
import type { SelectedScope } from "../../../../types/scope";

const DIRECTION = {
  UP: "Up",
  DOWN: "Down",
  EQ: "No Change",
} as const;

type Direction = (typeof DIRECTION)[keyof typeof DIRECTION];

// Parsed summary payload is a list of row tuples: [groupValue, count].
type SummaryRow = [string | number, number];
// Parsed trend payload is a list of row tuples: [period, count].
type TrendRow = [string | number, number];

export interface KPICardProps {
  selectedScope?: SelectedScope | null;
  filterToExecute?: string | null;
  scopeToExecute?: string | null;
  groupBy: string[];
  severity: number | "all";
  widgetKey?: string;
  setIsReadyToExport?: ((isReady: boolean) => void) | null;
}

const KPICard: React.FC<KPICardProps> = ({
  selectedScope,
  filterToExecute,
  scopeToExecute,
  groupBy,
  severity,
  widgetKey,
  setIsReadyToExport,
}) => {
  const { getSeverityField } = useSeverityAsPriority();
  const { dashboardKpis } = useFlags();

  const { onFilterClick } = useWidgetFilterNavigator({
    filterToExecute: filterToExecute,
    filterId: null,
    scopeToExecute: scopeToExecute,
    scopeId: selectedScope?.value?.id,
  });

  const { summaryData, summaryDataError, summaryLoading, refetchSummary } =
    useSummary({
      variables: {
        name: "FINDINGS_VIEW",
        selection: ["finding.id"],
        group_by: groupBy,
        aggregate_functions: ["COUNT"],
        round_functions: ["ROUND"],
        filters_config: {
          filtersjson: filterToExecute || null,
          scopesjson: scopeToExecute || null,
          scopesid: selectedScope?.value?.id,
        },
      },
      skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
    });

  const data = summaryData as SummaryRow[] | null;

  const { trendData, trendLoading, trendDataError, refetchTrend } = useTrend({
    variables: {
      ...(widgetKey ? { widgetKey } : {}),
      selection: ["open"],
      timescale: timeScales.MONTH,
      filters_config: {
        filtersjson: filterToExecute,
        scopesjson: scopeToExecute,
        scopesid: selectedScope?.value?.id,
        filters_fields_to_override: [
          ...(severity === "all"
            ? []
            : [
                JSON.stringify({
                  value: [severity?.toString()],
                  field: "remediation_item.severity",
                  condition: "in",
                }),
              ]),
        ],
      },
      aggregate_functions: ["SUM"],
      round_functions: ["ROUND"],
    },
    skip:
      !dashboardKpis ||
      !filterToExecute ||
      (!scopeToExecute && !selectedScope?.value?.id),
  });

  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [diffCount, setDiffCount] = useState<number | null>(null);
  const [direction, setDirection] = useState<Direction | null>(null);

  useEffect(() => {
    if (!!data) {
      if (severity === "all") {
        setTotalCount(data?.[0]?.[0] as number);
      } else {
        const severityItem = data?.find((res) => res?.[0] === severity);
        if (!!severityItem) {
          setTotalCount(severityItem?.[1]);
        } else {
          setTotalCount(0);
        }
      }
    }
  }, [data, severity]);

  useEffect(() => {
    if (!!trendData) {
      let diffData = (trendData as TrendRow[])?.reverse();
      let findingsCount;
      let lastPeriodFindingsCount;

      findingsCount = diffData?.[0]?.[1] || 0;
      lastPeriodFindingsCount = diffData?.[1]?.[1] || 0;

      const diff = findingsCount - lastPeriodFindingsCount;
      const direction =
        diff === 0 ? DIRECTION.EQ : diff > 0 ? DIRECTION.UP : DIRECTION.DOWN;
      setDiffCount(diff);
      setDirection(direction);
    }
  }, [trendData, severity]);

  useEffect(() => {
    if (dashboardKpis) {
      setIsReadyToExport?.(totalCount !== null && diffCount !== null);
    } else {
      setIsReadyToExport?.(totalCount !== null);
    }
  }, [dashboardKpis, setIsReadyToExport, totalCount, diffCount]);

  useEffect(() => {
    if (!!summaryDataError || !!trendDataError) {
      setIsReadyToExport?.(true);
    }
  }, [summaryDataError, trendDataError]);

  useEffect(() => {
    setIsReadyToExport?.((dashboardKpis && !trendLoading) || !summaryLoading);
  }, [trendLoading, summaryLoading, dashboardKpis]);

  const handleClick = () => {
    if (!!totalCount && totalCount > 0) {
      onFilterClick([
        [
          "in",
          severity === "all" ? null : "remediation_item.severity",
          severity === "all" ? null : severity?.toString(),
        ],
      ] as unknown as FilterCondition[]);
    }
  };

  return (
    <div className={"kpi-card-widget"} onClick={handleClick}>
      {!summaryDataError ? (
        totalCount === 0 && !summaryLoading ? (
          <Loader
            data-testid={"kpi-no-data"}
            className={"empty-state-no-match-to-filter"}
            center
            noBackground
            icon={"seem-icon-manualfinding"}
            subtitle={"There are no Findings for this Scope and Filter"}
          />
        ) : (
          <div
            className={`kpi-card-wrapper`}
            title={
              !!totalCount && totalCount > 0
                ? "View findings"
                : "0 Findings available"
            }
          >
            {summaryLoading || totalCount === null ? (
              <SkeletonLoader width={"160px"} height={"42px"} />
            ) : (
              !!totalCount &&
              totalCount > 0 && (
                <div className={"kpi-title"}>{formatNumber(totalCount)}</div>
              )
            )}
            {!!dashboardKpis && (
              <div
                className={`kpi-trend ${snakeCase(direction?.toLowerCase())}`}
              >
                {!trendDataError ? (
                  trendLoading || summaryLoading || !trendData ? (
                    <SkeletonLoader width={"150px"} />
                  ) : (
                    !!trendData &&
                    !!direction && (
                      <div className={"kpi-trend-inner"}>
                        <i
                          className={`seem-icon seem-icon-performance-${snakeCase(
                            direction?.toLowerCase()
                          )}`}
                        />
                        <span>
                          {`${direction}
                    ${
                      direction === DIRECTION.EQ
                        ? ""
                        : normalizeNumbers(diffCount!)
                    }
                      from Last Month`}
                        </span>
                      </div>
                    )
                  )
                ) : (
                  <div className={"kpi-trend-error"}>Error loading trends</div>
                )}
              </div>
            )}
            <div className={"kpi-card-legend"}>
              <div
                className={`kpi-icon ${severity === "all" ? "all" : ""}`}
                style={{
                  backgroundColor: getSeverityField(
                    severity,
                    SeverityFieldsEnum.COLOR
                  ),
                }}
              />
              <span className={"kpi-card-legend-text"}>{`${
                severity === "all"
                  ? "All"
                  : capitalize(
                      getSeverityField(severity, SeverityFieldsEnum.NAME)
                    )
              } Findings`}</span>{" "}
            </div>
          </div>
        )
      ) : (
        <WidgetError
          onWidgetsReload={() => {
            refetchSummary();
            refetchTrend();
          }}
        />
      )}
    </div>
  );
};

export default KPICard;
